import { useRef, useState } from 'react'
import { Box, Flex, Text } from '@chakra-ui/react'
import { useFormColors } from '../../forms/FormThemeContext'
import { useResolveText } from '../../i18n'

interface FileInputProps {
  label?: string
  labelTid?: string
  required?: boolean
  accept?: string
  /** Tamaño máximo permitido en megabytes. Si se supera, el archivo se rechaza. */
  maxSizeMb?: number
  value?: File | null
  onChange: (file: File | null) => void
  error?: string
  placeholder?: string
  placeholderTid?: string
  /** Optional name of a file that already exists server-side. Rendered as a hint when
   * no new File is selected, so users know the current attachment is preserved unless
   * they explicitly upload a replacement. */
  existingFileName?: string
  disabled?: boolean
}

/** Convierte un atributo `accept` (".pdf,.jpg") en una etiqueta legible ("PDF, JPG"). */
function formatAcceptLabel(accept: string): string {
  return accept
    .split(',')
    .map((s) => s.trim().replace(/^\./, '').toUpperCase())
    .filter(Boolean)
    .join(', ')
}

export function FileInput({ label, labelTid, required, accept = '.pdf,.jpg,.png', maxSizeMb, value, onChange, error, placeholder, placeholderTid, existingFileName, disabled }: FileInputProps) {
  const c = useFormColors()
  const resolve = useResolveText()
  const resolvedLabel = resolve(labelTid, label)
  const resolvedPlaceholder = resolve(placeholderTid, placeholder) ?? 'Seleccionar archivo'
  const inputRef = useRef<HTMLInputElement>(null)
  const [validationError, setValidationError] = useState<string | null>(null)

  const displayError = error ?? validationError ?? undefined
  const hint = `${formatAcceptLabel(accept)}${maxSizeMb ? ` · Máx. ${maxSizeMb} MB` : ''}`

  const handleClick = () => inputRef.current?.click()

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null
    if (inputRef.current) inputRef.current.value = ''

    if (file) {
      if (file.size === 0) {
        setValidationError('El archivo está vacío (0 KB). Seleccione un archivo con contenido.')
        onChange(null)
        return
      }
      const allowed = accept.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean)
      const ext = '.' + (file.name.split('.').pop() ?? '').toLowerCase()
      if (allowed.length > 0 && !allowed.includes(ext)) {
        setValidationError(`Formato no permitido. Use: ${formatAcceptLabel(accept)}.`)
        onChange(null)
        return
      }
      if (maxSizeMb && file.size > maxSizeMb * 1024 * 1024) {
        setValidationError(`El archivo supera el tamaño máximo de ${maxSizeMb} MB.`)
        onChange(null)
        return
      }
    }

    setValidationError(null)
    onChange(file)
  }

  const handleRemove = () => {
    setValidationError(null)
    onChange(null)
  }

  return (
    <Box mb={2}>
      {resolvedLabel && (
        <Text fontSize="sm" fontWeight="500" color={c.textSecondary} mb={2}>
          {resolvedLabel}{required && <Text as="span" color={c.error}> *</Text>}
        </Text>
      )}
      <input ref={inputRef} type="file" accept={accept} onChange={handleChange} style={{ display: 'none' }} />

      {value ? (
        <Flex
          alignItems="center" gap={3} px={4} py={3}
          bg={c.bgInput} borderRadius="sm" border="1px solid" borderColor={c.border}
        >
          <Box flexShrink={0} color={c.accent}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
            </svg>
          </Box>
          <Box flex={1} minW={0}>
            <Text fontSize="sm" color={c.text} fontWeight="500" truncate>{value.name}</Text>
            <Text fontSize="xs" color={c.textDimmed}>{(value.size / 1024).toFixed(0)} KB</Text>
          </Box>
          <button type="button" onClick={handleRemove} style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', color: c.error, cursor: 'pointer', background: 'transparent', border: 'none' }}>
            Quitar
          </button>
        </Flex>
      ) : existingFileName ? (
        <Flex
          alignItems="center" gap={3} px={4} py={3}
          bg={c.bgInput} borderRadius="sm"
          border="1px solid" borderColor={displayError ? c.error : c.border}
          cursor={disabled ? 'not-allowed' : 'pointer'}
          opacity={disabled ? 0.6 : 1}
          _hover={disabled ? {} : { borderColor: c.accent }}
          onClick={disabled ? undefined : handleClick}
          role="button"
        >
          <Box flexShrink={0} color={c.accent}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
            </svg>
          </Box>
          <Box flex={1} minW={0}>
            <Text fontSize="sm" color={c.text} fontWeight="500" truncate>{existingFileName}</Text>
            <Text fontSize="xs" color={c.textDimmed}>Archivo cargado · se mantendrá si no subes otro</Text>
          </Box>
          <Text fontSize="xs" color={c.accent} fontWeight="600">
            Reemplazar
          </Text>
        </Flex>
      ) : (
        <Flex
          alignItems="center" justifyContent="center" gap={2}
          w="100%" px={4} py={3}
          bg={c.bgInput} borderRadius="sm"
          border="1px dashed" borderColor={displayError ? c.error : c.border}
          color={c.textDimmed} fontSize="sm"
          cursor={disabled ? 'not-allowed' : 'pointer'}
          opacity={disabled ? 0.6 : 1}
          _hover={disabled ? {} : { borderColor: c.accent, color: c.accent }}
          transition="all 0.2s"
          onClick={disabled ? undefined : handleClick}
          role="button"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          {resolvedPlaceholder}
        </Flex>
      )}

      {!value && (
        <Text fontSize="xs" color={c.textDimmed} mt={1}>{hint}</Text>
      )}
      {displayError && <Text fontSize="xs" color={c.error} mt={1}>{displayError}</Text>}
    </Box>
  )
}
